/**
 * Optimized checkout mobile order summary uses a drawer toggle without a stable hook.
 * Tag the first control in `.cartDrawer-body` so E2E can use
 * getByTestId('checkout-order-summary-show-details').
 */
const SHOW_DETAILS_TEST_ID = "checkout-order-summary-show-details";

/** Selector list: cart preview, checkout root, drawer markup, or modal portals that host it. */
const CART_DRAWER_CONTEXT_SELECTOR = [
  "#cart-preview-dropdown",
  "#checkout-app",
  ".cartDrawer-body",
  ".cartDrawer",
  ".ReactModalPortal",
].join(", ");

/**
 * Returns true only when a mutation might add or reshape cart drawer markup.
 * Avoids running `tagCartDrawerShowDetailsToggle` (which queries `.cartDrawer-body`
 * globally) on unrelated page churn.
 *
 * @param {MutationRecord[]} mutations
 */
function mutationsMightAffectCartDrawer(mutations) {
  /** @param {Element} el */
  const isInCartContext = (el) =>
    Boolean(el?.closest?.(CART_DRAWER_CONTEXT_SELECTOR));

  for (const m of mutations) {
    const target = m.target;
    if (target?.nodeType === Node.ELEMENT_NODE) {
      if (isInCartContext(/** @type {Element} */ (target))) {
        return true;
      }
    }

    for (const n of m.addedNodes) {
      if (n.nodeType !== Node.ELEMENT_NODE) {
        continue;
      }
      const el = /** @type {Element} */ (n);
      if (
        isInCartContext(el) ||
        el.matches?.(".cartDrawer-body, .cartDrawer, .ReactModalPortal") ||
        el.querySelector?.(".cartDrawer-body, .cartDrawer") ||
        el.id === "cart-preview-dropdown" ||
        el.id === "checkout-app"
      ) {
        return true;
      }
    }
  }
  return false;
}

function tagCartDrawerShowDetailsToggle() {
  document.querySelectorAll(".cartDrawer-body").forEach((body) => {
    if (body.querySelector(`[data-testid="${SHOW_DETAILS_TEST_ID}"]`)) {
      return;
    }
    const toggle = body.querySelector("a") ?? body.querySelector("button");
    if (toggle) {
      toggle.dataset.testid = SHOW_DETAILS_TEST_ID;
    }
  });
}

export default function initCartDrawerTestIds() {
  tagCartDrawerShowDetailsToggle();

  if (typeof MutationObserver === "undefined") {
    return;
  }

  const observer = new MutationObserver((mutations) => {
    if (!mutationsMightAffectCartDrawer(mutations)) {
      return;
    }
    tagCartDrawerShowDetailsToggle();
  });

  // `document.body` + subtree covers header cart, checkout, and React portals under
  // `body`. The callback filters mutations so we do not run the tagging pass on
  // unrelated updates (per review: narrow work, not necessarily a narrower observe root).
  observer.observe(document.body, { childList: true, subtree: true });
}
